<template>
  <div
    :class="wrapperComputedClass"
    style="height: fit-content; width: fit-content"
  >
    <svg :class="circleComputedClass" :width="svgSize" :height="svgSize">
      <circle
        :cx="radius"
        :cy="radius"
        :r="radiusAdjusted"
        :stroke="colors[0]"
        :stroke-width="width"
        fill="transparent"
      />
      <path
        :d="arcPath((currentValue / maxValue) * 100)"
        :stroke="colors[1]"
        :stroke-width="width"
        stroke-linecap="round"
        fill="transparent"
      />
    </svg>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
  radius: {
    type: Number,
    default: 8.5,
  },
  value: {
    type: Number,
    default: 30,
  },
  maxValue: {
    type: Number,
    default: 100,
  },
  width: {
    type: Number,
    default: 2.5,
  },
  colors: {
    type: Array,
    default: () => ['#4D4D4D', '#4DB051'],
  },
  duration: {
    type: Number,
    default: 0,
  },
  direction: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
  wrapperClass: {
    type: String,
    default: '',
  },
  circleClass: {
    type: String,
    default: '',
  },
})

const currentValue = ref(100)

const svgSize = computed(() => props.radius * 2)
const radiusAdjusted = computed(() => props.radius - props.width / 2)
const startAngle = computed(() => -Math.PI / 2)
const circumference = computed(() => Math.PI * 2)
const circleComputedClass = computed(() => [
  'circle',
  props.circleClass,
  props.direction,
])
const wrapperComputedClass = computed(() => [
  props.wrapperClass,
  'h-fit',
  'w-fit',
])

function arcPath(percentage) {
  const end = startAngle.value - (percentage / 100) * circumference.value
  const largeArcFlag = startAngle.value - end <= Math.PI ? 0 : 1
  const start = {
    x: props.radius + radiusAdjusted.value * Math.cos(startAngle.value),
    y: props.radius + radiusAdjusted.value * Math.sin(startAngle.value),
  }
  const endCoord = {
    x: props.radius + radiusAdjusted.value * Math.cos(end),
    y: props.radius + radiusAdjusted.value * Math.sin(end),
  }
  return `M ${start.x || 0} ${start.y || 0} A ${radiusAdjusted.value || 0} ${
    radiusAdjusted.value || 0
  } 0 ${largeArcFlag || 0} 0 ${endCoord.x || 0} ${endCoord.y || 0}`
}

function animateProgress(newValue) {
  const start = currentValue.value
  const change = newValue - start
  const startTime = performance.now()

  function animateStep(currentTime) {
    const elapsedTime = currentTime - startTime
    const progress = Math.min(elapsedTime / props.duration, 1)

    currentValue.value = start + change * progress

    if (progress < 1) {
      requestAnimationFrame(animateStep)
    }
  }

  requestAnimationFrame(animateStep)
}

onMounted(() => animateProgress(props.value))
watch(
  () => props.value,
  (newValue) => {
    animateProgress(newValue)
  }
)
</script>

<style scoped lang="scss">
.circle {
  &.left {
    transform: scale(-1, 1);
  }
}
</style>
